import React, { useRef, useState, useMemo, useEffect, useCallback } from "react";
import DataGrid, { DataGridItem } from "components/data-grid";
import { EventTypeSelect, EventSubDeploymentSelect } from "components/select";
import { injectIntl } from "react-intl";
import PropTypes from "prop-types";
import CommonForm from "../common-form";
import { useQuery } from "components/graphql/query/use-query";
import { getEventTypeQuery } from "../utils";
import { PageSection } from "components/page";
import { ActionWithModalControlled } from "components/buttons";
import withStyles from "components/with-styles";
import styles from "./styles/form.css.js";
import { DeploymentAutocompleteSelect } from "components/autocomplete";
import TextField from "@material-ui/core/TextField";
import InputLabel from "@material-ui/core/InputLabel";
import { storeTiiviiOttConfigActionCreator } from "../../../../actions/common-actions";
import { useDispatch } from "react-redux";
import {
  getContractHasTiiviiOtt,
  getContractSubDeploymentsFromContractId,
  getRoomTemplatesService,
} from "../../../../services/commonService";
import { ContractSelect } from "../../../../components/select";
import { useCurrentCustomerContext } from "../../../../components/current-customer-context";
import ContractPaginatedTable from "../../../contract/table";

const NewEventForm = ({
  intl,
  values,
  handleChange,
  setHasRoomTemplates,
  isSportEvent,
  setSportEvent,
  classes,
  showLocalRecEventFields,
  setShowLocalRecEventFields,
}) => {
  const dispatch = useDispatch();
  const prevContractId = useRef(values.contractId);

  const { contracts, deployments } = useCurrentCustomerContext();

  const data = {
    contract: contracts.find(contract => contract._id === values.contractId),
  };

  const deployment = deployments?.find(d => d._id === data.contract?.deployment_id);

  const [openContractModal, setOpenContractModal] = useState(false);
  const [hasTiiviiOttConfig, setHasTiiviiOttConfig] = useState(false);
  const [deploymentIdForContractFilter, setDeploymentIdForContractFilter] = useState();
  const [contractNameForContractFilter, setContractNameForContractFilter] = useState();
  const [contractIdSelected, setContractIdSelected] = useState(values.contractId);
  const [subdeployments, setSubdeployments] = useState([]);

  const eventTypeQuery = useMemo(() => getEventTypeQuery(values.typeId), [values.typeId]);
  const { data: eventTypeData } = useQuery(eventTypeQuery);

  const handleContractHasTiiviiOttConfig = data => {
    setHasTiiviiOttConfig(data.has_tiivii_ott_configured);
  };

  const handleServiceHasTiiviiOttConfig = useCallback(
    () => {
      getContractHasTiiviiOtt({ contractId: values.contractId }, handleContractHasTiiviiOttConfig);
    },
    [values.contractId],
  );

  const renameStreamSourceIds = arr => {
    return arr.map(obj => {
      const { streamsource_ids, ...rest } = obj;
      return {
        ...rest,
        streamsourceIds: streamsource_ids,
      };
    });
  };

  const setContractSubDeployments = response => {
    setSubdeployments(renameStreamSourceIds(response));
  };

  const callToGetContractSubDeployments = () => {
    getContractSubDeploymentsFromContractId(
      {
        contract_id: values.contractId,
      },
      setContractSubDeployments,
    );
  };

  useEffect(
    () => {
      if (!!values?.contractId) {
        handleServiceHasTiiviiOttConfig();
        callToGetContractSubDeployments();
      }
    },
    [values.contractId],
  );

  const storeTiiviiOttConfig = data => {
    dispatch(storeTiiviiOttConfigActionCreator(data));
  };

  useEffect(
    () => {
      if (prevContractId !== values.contractId) {
        prevContractId.current = values.contractId;
        handleChange("typeId")("");
        handleChange("roomTemplateId")("");
        handleChange("graphicSetId")("");
      }
    },
    [handleChange, values.contractId],
  );

  useEffect(
    () => {
      if (data.contract) {
        if (data.contract.customer_id !== values.customerId) {
          handleChange("customerId")(data.contract.customer_id);
        }

        if (!data.contract?.local_rec_available && values.localRec) {
          handleChange("localRec")(false);
        }

        if (!deployment?.allow_qod && values.activeQod) {
          handleChange("activeQod")(false);
        }

        const roomTemplatesCallback = r => {
          setHasRoomTemplates(!!r?.length);
        };

        const roomTemplatesErrorCallback = () => {
          setHasRoomTemplates(false);
        };

        getRoomTemplatesService(values.contractId, true, roomTemplatesCallback, roomTemplatesErrorCallback);
      }
    },
    [data.contract, deployment, handleChange, setHasRoomTemplates, values.contractId, values.customerId],
  );

  useEffect(
    () => {
      const currentSportEvent =
        values.typeId && eventTypeData && eventTypeData.eventType && eventTypeData.eventType.isSportEvent;
      if (isSportEvent !== currentSportEvent) {
        setSportEvent(!!currentSportEvent);
      }
    },
    [eventTypeData, isSportEvent, setSportEvent, values.typeId],
  );

  useEffect(
    () => {
      if (hasTiiviiOttConfig) {
        storeTiiviiOttConfig(hasTiiviiOttConfig);
      }
      return () => storeTiiviiOttConfig(null);
    },
    [hasTiiviiOttConfig],
  );

  const handleClick = () => {
    if (contracts.length > 5) {
      setOpenContractModal(true);
    }
  };

  const handleClickOnRow = item => {
    setContractIdSelected(item._id);
  };

  const handleChangeContract = e => {
    handleChange("contractId")(e.target.value);
  };

  const handleContractModal = value => {
    setOpenContractModal(value);
  };

  const parsedCustomer = {
    contract: data.contract
      ? {
          id: data.contract._id,
          customerId: data.contract.customer_id,
          hasRoomTemplates: false,
          localRecAvailable: data.contract.local_rec_available || false,
          recordAndShareAvailable: data.contract.record_and_share_available || false,
          allowQod: deployment?.allow_qod || false,
          msisdnQod: deployment?.msisdn_qod || "",
          tiiviiOttConfig: {
            enabled: data.contract.tiivii_ott_config?.enabled || false,
            webhookUrl: data.contract.tiivii_ott_config?.webhook_url || "",
            nimbleConfigUrl: data.contract.tiivii_ott_config?.nimble_config_url || "",
            __typename: "TiiviiOttConfig",
          },
          __typename: "Contract",
        }
      : {},
  };

  return (
    <>
      <PageSection>
        <DataGrid>
          <DataGridItem className={classes.searchBox}>
            <ContractSelect
              key={values.contractId}
              disabled={contracts?.length === 1 || contracts?.length > 5}
              label={intl.formatMessage({
                id: "event.contract",
              })}
              name="contractId"
              handleChange={handleChangeContract}
              value={values.contractId}
              handleClick={handleClick}
              multiple={false}
            />
            {contracts?.length > 5 && (
              <ActionWithModalControlled
                open={openContractModal}
                setOpen={handleContractModal}
                onOpen={() => {
                  setContractIdSelected(values.contractId);
                }}
                onClose={() => void 0}
                onAccept={() => {
                  handleChange("contractId")(contractIdSelected);
                }}
                dialogTitle={intl.formatMessage({
                  id: "event.contract",
                })}
                icon="search"
                renderDialogContent={() => (
                  <DataGrid>
                    <DataGridItem big>
                      <DeploymentAutocompleteSelect
                        id="deploymentIdForContractFilter"
                        name="deploymentIdForContractFilter"
                        placeholder={intl.formatMessage({ id: "app.page.contract.search.deployment" })}
                        label={intl.formatMessage({
                          id: "contract.deployment",
                        })}
                        value={deploymentIdForContractFilter}
                        onChange={() => value => setDeploymentIdForContractFilter(value)}
                      />
                    </DataGridItem>
                    <DataGridItem big>
                      <div>
                        <InputLabel className={classes.select_label} htmlFor="contractNameForContractFilter">
                          {intl.formatMessage({ id: "common.name" })}
                        </InputLabel>
                        <TextField
                          id="contractNameForContractFilter"
                          name="contractNameForContractFilter"
                          fullWidth
                          onChange={ev => setContractNameForContractFilter(ev.target.value)}
                          value={contractNameForContractFilter}
                          placeholder={intl.formatMessage({ id: "app.page.contract.search.name" })}
                        />
                      </div>
                    </DataGridItem>
                    <DataGridItem big>
                      <ContractPaginatedTable
                        onClickRow={handleClickOnRow}
                        selectedRowId={contractIdSelected}
                        contractName={contractNameForContractFilter}
                        deploymentId={deploymentIdForContractFilter}
                      />
                    </DataGridItem>
                  </DataGrid>
                )}
              />
            )}
          </DataGridItem>
          <DataGridItem>
            <EventSubDeploymentSelect
              isInsideForm
              label={intl.formatMessage({
                id: "event.subdeployment",
              })}
              onChange={handleChange}
              contractId={values.contractId}
              value={values.subdeployment}
              classes={classes}
              subDeployments={subdeployments}
              emptyValueText={intl.formatMessage({ id: "common.fullDeployment" })}
            />
          </DataGridItem>
          <DataGridItem>
            <EventTypeSelect
              isInsideForm
              label={intl.formatMessage({
                id: "event.typeId",
              })}
              onChange={handleChange}
              contractId={values.contractId}
              value={values.typeId}
            />
          </DataGridItem>
        </DataGrid>
      </PageSection>
      <CommonForm
        customer={parsedCustomer}
        isSportEvent={isSportEvent}
        isNewForm
        values={values}
        handleChange={handleChange}
        eventCustomerId={values.customerId}
        showLocalRecEventFields={showLocalRecEventFields}
        setShowLocalRecEventFields={setShowLocalRecEventFields}
      />
    </>
  );
};

NewEventForm.propTypes = {
  /**
   * Callback que se dispara cuando cambia el contrato seleccionado
   */
  setHasRoomTemplates: PropTypes.func,
  /**
   * Valores del formulario
   */
  values: PropTypes.object,
  /**
   * Callback que se dispara con el cambio del input
   */
  handleChange: PropTypes.func,
  /**
   * Indica si es un evento deportivo
   */
  isSportEvent: PropTypes.bool,
  /**
   * Callback para cambiar la prop isSportEvent
   */
  setSportEvent: PropTypes.func,
};

export default injectIntl(withStyles(styles)(NewEventForm));
